// Client for the public weather surface (mounted OUTSIDE /v1 — free upstream cyclone data).

import { LANES } from '../data/lanes'

const API_BASE = (import.meta.env.VITE_API_BASE as string) ?? 'https://schedulesmcp-api.fly.dev/v1'
const WEATHER_BASE = API_BASE.replace(/\/v1\/?$/, '')

export interface CycloneTrackPoint {
  kind: 'history' | 'forecast'
  lat: number
  lon: number
  at: string | null
  windMs: number | null
  category: string | null
}

export interface AlertArea {
  kind: string // 'wind' | 'affected' | 'cone'
  level: string // 'Green' | 'Orange' | 'Red' | …
  geojson: { type?: string; coordinates?: unknown } | null
}

export interface Cyclone {
  id: string
  source: string
  name: string
  basin: string | null
  number: string | null
  category: string | null
  center: { lat: number; lon: number }
  windMs: number | null
  pressureHpa: number | null
  moveKmh: number | null
  moveDir: string | null
  galeRadiusKm: number | null
  observedAt: string | null
  track: CycloneTrackPoint[]
  alertAreas?: AlertArea[]
}

// ── Extratropical (winter) marine storms — NOAA OPC surface-analysis lows (parallel to cyclones) ──

export interface OceanStormTrackPoint {
  hour: number // 0 = now, then 24/48…
  lat: number
  lon: number
  pressureHpa: number | null
}

export interface OceanStorm {
  id: number
  basin: string // 'North Atlantic' | 'North Pacific'
  level: 'gale' | 'storm' | 'hurricane_force'
  center: { lat: number; lon: number }
  pressureHpa: number | null
  maxWindKt: number | null
  moveDir: string | null
  moveKt: number | null
  galeRadiusKm: number | null
  observedAt: string | null
  track: OceanStormTrackPoint[]
}

export interface StormExposure {
  pol: string
  pod: string
  cyclone_id: string
  min_dist_km: number
  level: string | null
  exposed_at: string | null
  segment: string
  cyclone: { name: string; number: string | null } | null
}

export async function fetchCyclones(): Promise<Cyclone[]> {
  const res = await fetch(`${WEATHER_BASE}/weather/cyclones`)
  if (!res.ok) throw new Error(`weather: HTTP ${res.status}`)
  const data = (await res.json()) as { cyclones?: Cyclone[] }
  return data.cyclones ?? []
}

/** Extratropical / winter marine storms (OPC). Empty on failure, mirroring the other soft feeds. */
export async function fetchOceanStorms(): Promise<OceanStorm[]> {
  try {
    const res = await fetch(`${WEATHER_BASE}/weather/ocean-storms`)
    if (!res.ok) return []
    const data = (await res.json()) as { storms?: OceanStorm[] }
    return data.storms ?? []
  } catch {
    return []
  }
}

export async function fetchLaneExposure(pol: string, pod: string): Promise<StormExposure[]> {
  const qs = new URLSearchParams({ pol, pod })
  const res = await fetch(`${WEATHER_BASE}/weather/exposure?${qs.toString()}`)
  if (!res.ok) return []
  const data = (await res.json()) as { exposures?: StormExposure[] }
  return data.exposures ?? []
}

// ── Intensity → design-token color + label (shared by map + badge) ────────────

export interface IntensityStyle {
  color: string // a CSS var()
  label: string
}

/** Category code → color (dark-theme intensity ramp) + human label. */
export function intensityStyle(category: string | null): IntensityStyle {
  switch (category) {
    case 'TD':
      return { color: 'var(--info)', label: 'Tropical Depression' }
    case 'TS':
      return { color: 'var(--signal)', label: 'Tropical Storm' }
    case 'STS':
      return { color: 'var(--warn)', label: 'Severe Tropical Storm' }
    case 'TY':
      return { color: 'var(--overdue)', label: 'Typhoon' }
    case 'STY':
      return { color: 'var(--danger)', label: 'Severe Typhoon' }
    case 'SuperTY':
      return { color: '#d926a9', label: 'Super Typhoon' }
    default:
      return { color: 'var(--text-muted)', label: category ?? 'Unknown' }
  }
}

/** Extratropical storm level → warm intensity ramp + human label (shared by map + cards). */
export function oceanStormStyle(level: OceanStorm['level']): { color: string; label: string } {
  switch (level) {
    case 'gale':
      return { color: '#d2591a', label: 'Gale' }
    case 'storm':
      return { color: '#e0662a', label: 'Storm' }
    case 'hurricane_force':
      return { color: '#c0392b', label: 'Hurricane-force' }
    default:
      return { color: '#d2591a', label: level }
  }
}

/** Beaufort-ish force from sustained wind (m/s) for a compact readout. */
export function windForce(windMs: number | null): number | null {
  if (windMs == null) return null
  const bins = [0.3, 1.6, 3.4, 5.5, 8, 10.8, 13.9, 17.2, 20.8, 24.5, 28.5, 32.7]
  let f = 0
  for (const b of bins) if (windMs >= b) f++
  return f
}

// ── Trade-lane geometry (client-side storm exposure on the map) ───────────────
//
// The light lane index (lanes-index.json) carries port CODES + sailing/carrier counts but no
// coordinates (those live in per-port JSON fetched only on the port page). To draw lanes on the
// storm map without a network fan-out we bake the coordinates of the ports that appear in the
// busiest ~60 lanes into a static lookup — 25 ports total. A lane whose endpoints aren't both in
// the lookup is skipped (it's not one of the top lanes by sailings anyway).
const PORT_COORDS: Record<string, [number, number]> = {
  // [lat, lon]
  AUSYD: [-33.85, 151.2],
  BEANR: [51.21667, 4.41667],
  BRSSZ: [-23.93333, -46.31667],
  CNNGB: [29.87, 121.55],
  CNSHA: [31.23, 121.47],
  CNTAO: [36.07, 120.38],
  DEHAM: [53.51667, 9.93333],
  FRLEH: [49.5, 0.1],
  GBFXT: [51.95, 1.32], // Felixstowe
  ESVLC: [39.45, -0.33], // Valencia
  GRPIR: [37.93333, 23.61667],
  HKHKG: [22.31667, 114.16667],
  IDJKT: [-6.13333, 106.83333],
  INNSA: [18.95, 72.95], // Jawaharlal Nehru (Nhava Sheva)
  KRPUS: [35.13333, 129.05],
  MXZLO: [19.05, -104.3],
  MYPKG: [3.0, 101.4],
  NLRTM: [51.91667, 4.5],
  SGSIN: [1.28333, 103.85],
  THLCH: [13.08333, 100.88333],
  TWKHH: [22.6, 120.3], // Kaohsiung
  USLAX: [34.05, -118.25],
  USNYC: [40.7, -74.0],
  USSAV: [32.08, -81.09], // Savannah
  VNSGN: [10.76667, 106.66667],
}

/** Top-N lanes by sailings, drawable on the map: a subdivided [lon,lat] polyline between the two
 *  port coordinates (dest longitude unwrapped to the shorter direction so trans-Pacific lanes don't
 *  wrap the whole globe). Rendering + distance both use `points`. */
export interface LaneGeo {
  slug: string
  o: string
  d: string
  oName: string
  dName: string
  sailings: number
  carriers: number
  points: Array<[number, number]> // [lon, lat]
}

const LANE_CAP = 60
const LANE_SUBDIV = 18

let laneGeoCache: LaneGeo[] | null = null

/** Build (once) the drawable geometry for the busiest lanes whose both endpoints have coordinates. */
export function laneGeos(): LaneGeo[] {
  if (laneGeoCache) return laneGeoCache
  const out: LaneGeo[] = []
  const sorted = [...LANES].sort((a, b) => b.sailings - a.sailings)
  for (const l of sorted) {
    const o = PORT_COORDS[l.o]
    const d = PORT_COORDS[l.d]
    if (!o || !d) continue
    const [oLat, oLon] = o
    let [dLat, dLon] = d
    // Unwrap the destination longitude to within 180° of the origin so the drawn line takes the
    // short way around (e.g. Shanghai→LA crosses the dateline, not back across Eurasia).
    while (dLon - oLon > 180) dLon -= 360
    while (dLon - oLon < -180) dLon += 360
    const points: Array<[number, number]> = []
    for (let i = 0; i <= LANE_SUBDIV; i++) {
      const t = i / LANE_SUBDIV
      points.push([oLon + (dLon - oLon) * t, oLat + (dLat - oLat) * t])
    }
    out.push({
      slug: l.slug,
      o: l.o,
      d: l.d,
      oName: l.o_name,
      dName: l.d_name,
      sailings: l.sailings,
      carriers: l.carriers,
      points,
    })
    if (out.length >= LANE_CAP) break
  }
  laneGeoCache = out
  return out
}

// ── Great-circle distance + point-to-segment (for lane↔storm proximity) ───────

const R_EARTH_KM = 6371

function toRad(d: number): number {
  return (d * Math.PI) / 180
}

/** Haversine great-circle distance in km. */
export function haversineKm(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const dLat = toRad(bLat - aLat)
  const dLon = toRad(bLon - aLon)
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLon / 2) ** 2
  return 2 * R_EARTH_KM * Math.asin(Math.min(1, Math.sqrt(s)))
}

/** Distance (km) from point P to segment A→B, via a local equirectangular tangent-plane at P.
 *  Longitude deltas are normalized to [-180,180] so lane points beyond ±180 still measure right. */
function segDistKm(
  pLat: number,
  pLon: number,
  aLat: number,
  aLon: number,
  bLat: number,
  bLon: number
): number {
  const kx = 111.32 * Math.cos(toRad(pLat))
  const ky = 110.57
  const norm = (lon: number) => {
    let x = lon - pLon
    while (x > 180) x -= 360
    while (x < -180) x += 360
    return x
  }
  const ax = norm(aLon) * kx
  const ay = (aLat - pLat) * ky
  const bx = norm(bLon) * kx
  const by = (bLat - pLat) * ky
  const dx = bx - ax
  const dy = by - ay
  const len2 = dx * dx + dy * dy
  let t = len2 > 0 ? -(ax * dx + ay * dy) / len2 : 0
  t = Math.max(0, Math.min(1, t))
  const cx = ax + t * dx
  const cy = ay + t * dy
  return Math.hypot(cx, cy)
}

/** Minimum distance (km) from a storm center to a lane's polyline. */
export function laneMinDistKm(lane: LaneGeo, center: { lat: number; lon: number }): number {
  let min = Number.POSITIVE_INFINITY
  for (let i = 1; i < lane.points.length; i++) {
    const a = lane.points[i - 1]
    const b = lane.points[i]
    if (!a || !b) continue
    const d = segDistKm(center.lat, center.lon, a[1], a[0], b[1], b[0])
    if (d < min) min = d
  }
  return min
}

// Fallback gale radius (km) by category when a storm reports none, so lanes still get flagged.
const GALE_FALLBACK_KM: Record<string, number> = {
  TD: 90,
  TS: 140,
  STS: 190,
  TY: 240,
  STY: 300,
  SuperTY: 360,
}

/** Effective gale-force footprint radius (km) for exposure tests. */
export function galeKmFor(c: Cyclone): number {
  if (c.galeRadiusKm && c.galeRadiusKm > 0) return c.galeRadiusKm
  return GALE_FALLBACK_KM[c.category ?? ''] ?? 120
}

/** Interpolated storm center at `hours` from now, advancing along the forecast track by timestamp.
 *  Returns the current center for hours<=0 or when the forecast has no usable timestamps. */
export function cycloneCenterAt(c: Cyclone, hours: number): { lat: number; lon: number } {
  if (hours <= 0) return c.center
  const base = c.observedAt ? Date.parse(c.observedAt) : Date.now()
  const target = base + hours * 3_600_000
  const nodes: Array<{ t: number; lat: number; lon: number }> = [
    { t: base, lat: c.center.lat, lon: c.center.lon },
  ]
  for (const p of c.track) {
    if (p.kind !== 'forecast' || !p.at) continue
    const t = Date.parse(p.at)
    if (Number.isFinite(t)) nodes.push({ t, lat: p.lat, lon: p.lon })
  }
  nodes.sort((a, b) => a.t - b.t)
  const first = nodes[0]
  if (!first || nodes.length < 2) return c.center
  if (target <= first.t) return { lat: first.lat, lon: first.lon }
  for (let i = 1; i < nodes.length; i++) {
    const a = nodes[i - 1]
    const b = nodes[i]
    if (!a || !b) continue
    if (target <= b.t) {
      const span = b.t - a.t || 1
      const f = (target - a.t) / span
      // Interpolate longitude along the shorter arc.
      let dLon = b.lon - a.lon
      while (dLon > 180) dLon -= 360
      while (dLon < -180) dLon += 360
      return { lat: a.lat + (b.lat - a.lat) * f, lon: a.lon + dLon * f }
    }
  }
  const last = nodes[nodes.length - 1]
  return last ? { lat: last.lat, lon: last.lon } : c.center
}

// Fallback gale radius (km) by extratropical level when a storm reports none.
const OCEAN_GALE_FALLBACK_KM: Record<string, number> = {
  gale: 300,
  storm: 400,
  hurricane_force: 500,
}

/** Effective gale-force footprint radius (km) for an extratropical storm's exposure tests. */
export function oceanGaleKmFor(s: OceanStorm): number {
  if (s.galeRadiusKm && s.galeRadiusKm > 0) return s.galeRadiusKm
  return OCEAN_GALE_FALLBACK_KM[s.level] ?? 300
}

/** Interpolated extratropical-storm center at `hours` from now, advancing along its track by the
 *  track's `hour` field (0 = now). Mirrors cycloneCenterAt but keyed on hours, not timestamps. */
export function oceanStormCenterAt(s: OceanStorm, hours: number): { lat: number; lon: number } {
  if (hours <= 0) return s.center
  const nodes: Array<{ hour: number; lat: number; lon: number }> = [
    { hour: 0, lat: s.center.lat, lon: s.center.lon },
  ]
  for (const p of s.track) {
    if (p.hour > 0) nodes.push({ hour: p.hour, lat: p.lat, lon: p.lon })
  }
  nodes.sort((a, b) => a.hour - b.hour)
  const first = nodes[0]
  if (!first || nodes.length < 2) return s.center
  if (hours <= first.hour) return { lat: first.lat, lon: first.lon }
  for (let i = 1; i < nodes.length; i++) {
    const a = nodes[i - 1]
    const b = nodes[i]
    if (!a || !b) continue
    if (hours <= b.hour) {
      const span = b.hour - a.hour || 1
      const f = (hours - a.hour) / span
      let dLon = b.lon - a.lon
      while (dLon > 180) dLon -= 360
      while (dLon < -180) dLon += 360
      return { lat: a.lat + (b.lat - a.lat) * f, lon: a.lon + dLon * f }
    }
  }
  const last = nodes[nodes.length - 1]
  return last ? { lat: last.lat, lon: last.lon } : s.center
}

/** Slugs of lanes whose path passes within any storm's gale footprint at hour `h` from now.
 *  Extratropical (ocean) storms fold in with the same point-to-lane proximity test. */
export function affectedLanesAt(
  lanes: LaneGeo[],
  cyclones: Cyclone[],
  hours: number,
  oceanStorms: OceanStorm[] = []
): Set<string> {
  const hit = new Set<string>()
  const positions = cyclones.map((c) => ({ center: cycloneCenterAt(c, hours), gale: galeKmFor(c) }))
  for (const s of oceanStorms) {
    positions.push({ center: oceanStormCenterAt(s, hours), gale: oceanGaleKmFor(s) })
  }
  for (const lane of lanes) {
    for (const p of positions) {
      if (laneMinDistKm(lane, p.center) <= p.gale) {
        hit.add(lane.slug)
        break
      }
    }
  }
  return hit
}

/** Union of lanes exposed at any 6-hourly step across a 0..`maxHours` window. */
export function affectedLanesWindow(
  lanes: LaneGeo[],
  cyclones: Cyclone[],
  maxHours = 72,
  oceanStorms: OceanStorm[] = []
): Set<string> {
  const hit = new Set<string>()
  for (let h = 0; h <= maxHours; h += 6) {
    for (const slug of affectedLanesAt(lanes, cyclones, h, oceanStorms)) hit.add(slug)
  }
  return hit
}

// ── Open-Meteo (free, no API key): chokepoint conditions + open-ocean sea state ───────────────
//
// FOLLOW-UP (extratropical / winter storms): full year-round mid-latitude storm entities (NOAA OPC
// surface-analysis lows, warm/cold fronts, hurricane-force wind areas) need a backend ingestion job
// like weather-ingest does for tropical cyclones — they are not point features and don't belong in a
// client fetch. Until that lands, the chokepoint strip + open-ocean waypoints below give an
// always-on wind/wave signal from Open-Meteo so the page stays meaningful off-season.

const OM_FORECAST = 'https://api.open-meteo.com/v1/forecast'
const OM_MARINE = 'https://marine-api.open-meteo.com/v1/marine'

export type SeaStatus = 'calm' | 'breezy' | 'rough' | 'fog' | 'closed-risk' | 'unknown'

export interface OceanPoint {
  name: string
  lat: number
  lon: number
  windKt: number | null
  gustKt: number | null
  waveM: number | null
  visibilityM: number | null
  weatherCode: number | null
  status: SeaStatus
}

export interface Waypoint {
  name: string
  basin: string
  lat: number
  lon: number
  waveM: number | null
  windKt: number | null
  advisory: boolean // significant wave height ≥ 4 m
}

// The five classic maritime chokepoints.
export const CHOKEPOINTS: Array<{ name: string; lat: number; lon: number }> = [
  { name: 'Suez Canal', lat: 30.5, lon: 32.35 },
  { name: 'Panama Canal', lat: 9.08, lon: -79.68 },
  { name: 'Strait of Malacca', lat: 2.5, lon: 101.4 },
  { name: 'Bosphorus', lat: 41.1, lon: 29.06 },
  { name: 'Strait of Gibraltar', lat: 35.95, lon: -5.6 },
]

// Open-ocean waypoints on the busy basins — an always-on sea-state layer for year-round coverage.
export const OCEAN_WAYPOINTS: Array<{ name: string; basin: string; lat: number; lon: number }> = [
  { name: 'North Atlantic', basin: 'Atlantic', lat: 50, lon: -30 },
  { name: 'North Pacific', basin: 'Pacific', lat: 40, lon: -160 },
  { name: 'South China Sea', basin: 'W-Pacific', lat: 15, lon: 115 },
  { name: 'Indian Ocean', basin: 'Indian', lat: 5, lon: 75 },
  { name: 'Mediterranean', basin: 'Med', lat: 37, lon: 15 },
]

const MS_TO_KT = 1.94384

/** Thresholds: gusts >34kt or wave >4m ⇒ rough; ≥48kt or ≥6m ⇒ closed-risk; visibility <1km ⇒ fog. */
function seaStatus(
  gustKt: number | null,
  waveM: number | null,
  visM: number | null,
  windKt: number | null
): SeaStatus {
  const g = gustKt ?? windKt
  if ((g != null && g >= 48) || (waveM != null && waveM >= 6)) return 'closed-risk'
  if ((g != null && g > 34) || (waveM != null && waveM > 4)) return 'rough'
  if (visM != null && visM < 1000) return 'fog'
  if (g != null && g > 22) return 'breezy'
  if (g != null || waveM != null) return 'calm'
  return 'unknown'
}

// Open-Meteo returns an array of per-location objects when multiple coords are requested; a single
// coord returns one object. Normalize to an array either way.
async function omBatch(url: string): Promise<Array<Record<string, unknown>>> {
  const res = await fetch(url)
  if (!res.ok) throw new Error(`open-meteo HTTP ${res.status}`)
  const json = await res.json()
  return Array.isArray(json) ? json : [json]
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

/** Current wind + wave + visibility at the five chokepoints. Per-point failures degrade to nulls. */
export async function fetchChokepointConditions(): Promise<OceanPoint[]> {
  const lats = CHOKEPOINTS.map((c) => c.lat).join(',')
  const lons = CHOKEPOINTS.map((c) => c.lon).join(',')
  const fUrl = `${OM_FORECAST}?latitude=${lats}&longitude=${lons}&current=wind_speed_10m,wind_gusts_10m,visibility,weather_code&wind_speed_unit=kn`
  const mUrl = `${OM_MARINE}?latitude=${lats}&longitude=${lons}&current=wave_height`

  const [fRes, mRes] = await Promise.allSettled([omBatch(fUrl), omBatch(mUrl)])
  const f = fRes.status === 'fulfilled' ? fRes.value : []
  const m = mRes.status === 'fulfilled' ? mRes.value : []

  return CHOKEPOINTS.map((cp, i) => {
    const fc = (f[i]?.current ?? {}) as Record<string, unknown>
    const mc = (m[i]?.current ?? {}) as Record<string, unknown>
    const windKt = num(fc.wind_speed_10m)
    const gustKt = num(fc.wind_gusts_10m)
    const waveM = num(mc.wave_height)
    const visibilityM = num(fc.visibility)
    const weatherCode = num(fc.weather_code)
    return {
      name: cp.name,
      lat: cp.lat,
      lon: cp.lon,
      windKt,
      gustKt,
      waveM,
      visibilityM,
      weatherCode,
      status: seaStatus(gustKt, waveM, visibilityM, windKt),
    }
  })
}

/** Significant wave height + wind at the open-ocean waypoints; flags ≥4m as a "high seas" advisory. */
export async function fetchOceanWaypoints(): Promise<Waypoint[]> {
  const lats = OCEAN_WAYPOINTS.map((c) => c.lat).join(',')
  const lons = OCEAN_WAYPOINTS.map((c) => c.lon).join(',')
  const mUrl = `${OM_MARINE}?latitude=${lats}&longitude=${lons}&current=wave_height`
  const fUrl = `${OM_FORECAST}?latitude=${lats}&longitude=${lons}&current=wind_speed_10m&wind_speed_unit=kn`

  const [mRes, fRes] = await Promise.allSettled([omBatch(mUrl), omBatch(fUrl)])
  const m = mRes.status === 'fulfilled' ? mRes.value : []
  const f = fRes.status === 'fulfilled' ? fRes.value : []

  return OCEAN_WAYPOINTS.map((wp, i) => {
    const mc = (m[i]?.current ?? {}) as Record<string, unknown>
    const fc = (f[i]?.current ?? {}) as Record<string, unknown>
    const waveM = num(mc.wave_height)
    return {
      name: wp.name,
      basin: wp.basin,
      lat: wp.lat,
      lon: wp.lon,
      waveM,
      windKt: num(fc.wind_speed_10m),
      advisory: waveM != null && waveM >= 4,
    }
  })
}

/** All storm-exposed lanes from the backend (real source), no pol/pod filter. Empty on failure. */
export async function fetchAllExposure(): Promise<StormExposure[]> {
  try {
    const res = await fetch(`${WEATHER_BASE}/weather/exposure`)
    if (!res.ok) return []
    const data = (await res.json()) as { exposures?: StormExposure[] }
    return data.exposures ?? []
  } catch {
    return []
  }
}

// Keep MS_TO_KT referenced (Open-Meteo already returns knots via wind_speed_unit; exported for callers
// that receive m/s wind from the cyclone feed and want a knots readout).
export function msToKt(ms: number): number {
  return ms * MS_TO_KT
}
